import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, Save, X, ArrowLeft, Shield, Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import { 
  useCreateUserProfileMutation, 
  useUpdateUserProfileMutation,
  useGetUserProfileByIdQuery,
  useGetRolesQuery,
  useValidateUsernameQuery,
  useValidateEmailQuery,
  useGetAvailableColorsQuery,
  useAssignUserColorMutation,
  useRemoveUserColorMutation
} from '../../store/slices/apiSlice';
import type { UserProfileFormData } from '../../utils/types';
import { useToast } from '../../hooks/useToast';

// Validation schema using Zod [[memory:5985994]]
const userProfileSchema = z.object({
  username: z.string()
    .min(3, 'Username must be at least 3 characters')
    .max(150, 'Username must be less than 150 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
  
  email: z.string()
    .email('Please enter a valid email address')
    .max(254, 'Email must be less than 254 characters'),
  
  first_name: z.string()
    .min(1, 'First name is required')
    .max(150, 'First name must be less than 150 characters'),
  
  last_name: z.string()
    .min(1, 'Last name is required')
    .max(150, 'Last name must be less than 150 characters'),
  
  password: z.string()
    .optional()
    .refine((val) => !val || val.length >= 8, {
      message: 'Password must be at least 8 characters',
    }),
  
  phone: z.string()
    .optional()
    .refine((val) => !val || /^\+?1?\d{9,15}$/.test(val), {
      message: 'Phone number must be between 9-15 digits',
    }),
  
  address: z.string().optional(),
  
  date_of_birth: z.string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      const date = new Date(val);
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear();
      return age >= 16 && age <= 100;
    }, {
      message: 'User must be between 16 and 100 years old',
    }),
  
  emergency_contact_name: z.string()
    .optional()
    .refine((val) => !val || val.length <= 100, {
      message: 'Emergency contact name must be less than 100 characters',
    }),
  
  emergency_contact_phone: z.string()
    .optional()
    .refine((val) => !val || /^\+?1?\d{9,15}$/.test(val), {
      message: 'Emergency contact phone must be between 9-15 digits',
    }),
  
  role: z.string().min(1, 'Role is required'),
  
  allocated_holidays: z.number()
    .optional()
    .nullable()
    .refine((val) => val === null || val === undefined || val >= 0, {
      message: 'Allocated holidays cannot be negative',
    })
    .refine((val) => val === null || val === undefined || val <= 365, {
      message: 'Allocated holidays cannot exceed 365 days',
    }),
});

type UserFormData = z.infer<typeof userProfileSchema>;

interface UserFormPageProps {}

export const UserFormPage: React.FC<UserFormPageProps> = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const isEditing = Boolean(id);
  
  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    phone: '',
    address: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    role: '',
    allocated_holidays: null,
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState<string>('');

  // Async validation (debounced)
  const debounce = (fn: (...args: any[]) => void, delay: number) => {
    let timer: any;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  const [usernameToValidate, setUsernameToValidate] = useState('');
  const [emailToValidate, setEmailToValidate] = useState('');

  // API queries and mutations
  const {
    data: existingUser,
    isLoading: isLoadingUser,
    error: loadError
  } = useGetUserProfileByIdQuery(id!, { skip: !isEditing });

  const existingUserId = existingUser?.user_details.id;
  const { data: usernameCheck, error: usernameError, isLoading: isValidatingUsername } = useValidateUsernameQuery(
    { username: usernameToValidate, exclude_user_id: existingUserId },
    { skip: !usernameToValidate || usernameToValidate.length < 2 } // Re-enabled with proper conditions
  );
  const { data: emailCheck, error: emailError, isLoading: isValidatingEmail } = useValidateEmailQuery(
    { email: emailToValidate, exclude_user_id: existingUserId },
    { skip: !emailToValidate || emailToValidate.length < 3 || !emailToValidate.includes('@') }
  );

  const { data: rolesData } = useGetRolesQuery({
    page: 1,
    page_size: 100,
    is_active: true
  });

  const { data: availableColors, isLoading: isLoadingColors } = useGetAvailableColorsQuery();

  const [createUserProfile] = useCreateUserProfileMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [assignUserColor] = useAssignUserColorMutation();
  const [removeUserColor] = useRemoveUserColorMutation();

  // Load existing user data when editing
  React.useEffect(() => {
    if (isEditing && existingUser) {
      setFormData({
        username: existingUser.user_details.username,
        email: existingUser.user_details.email,
        first_name: existingUser.user_details.first_name,
        last_name: existingUser.user_details.last_name,
        phone: existingUser.phone || '',
        address: existingUser.address || '',
        date_of_birth: existingUser.date_of_birth || '',
        emergency_contact_name: existingUser.emergency_contact_name || '',
        emergency_contact_phone: existingUser.emergency_contact_phone || '',
        role: existingUser.role || '',
        allocated_holidays: existingUser.allocated_holidays ?? null,
        // Don't load password when editing
      });
      // Load existing color
      setSelectedColorId(existingUser.assigned_color || '');
    }
  }, [existingUser, isEditing]);

  // Handle form field changes
  const handleInputChange = (field: keyof UserFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Debounced uniqueness checks
    if (field === 'username') {
      debouncedUsername(value);
    }
    if (field === 'email') {
      debouncedEmail(value);
    }
  };

  const debouncedUsername = React.useMemo(
    () => debounce((val: string) => setUsernameToValidate(val.trim()), 400),
    []
  );
  const debouncedEmail = React.useMemo(
    () => debounce((val: string) => setEmailToValidate(val.trim()), 400),
    []
  );

  // Handle username validation results
  React.useEffect(() => {
    if (!usernameToValidate) return;
    
    // Clear username validation errors first
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.username;
      return newErrors;
    });
    
    // Handle validation response
    if (usernameCheck !== undefined) {
      if (!usernameCheck.is_unique) {
        setErrors(prev => ({ 
          ...prev, 
          username: 'This username is already taken' 
        }));
      }
    }
    
    // Handle validation API errors
    if (usernameError) {
      setErrors(prev => ({ 
        ...prev, 
        username: 'Error checking username availability' 
      }));
    }
  }, [usernameCheck, usernameError, usernameToValidate]);
  // Handle email validation results
  React.useEffect(() => {
    if (!emailToValidate) return;
    
    // Clear email validation errors first
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.email;
      return newErrors;
    });
    
    // Handle validation response
    if (emailCheck !== undefined) {
      if (!emailCheck.is_unique) {
        setErrors(prev => ({ 
          ...prev, 
          email: 'This email is already in use' 
        }));
      }
    }
    
    // Handle validation API errors
    if (emailError) {
      setErrors(prev => ({ 
        ...prev, 
        email: 'Error checking email availability' 
      }));
    }
  }, [emailCheck, emailError, emailToValidate]);

  // Validate form data
  const validateForm = (): boolean => {
    try {
      // For editing, password is optional
      const schemaToUse = isEditing 
        ? userProfileSchema
        : userProfileSchema.refine(
            (data) => data.password && data.password.length >= 8,
            {
              message: 'Password is required for new users',
              path: ['password'],
            }
          );

      schemaToUse.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Validation Error', 'Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API
      const apiData: UserProfileFormData = {
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        date_of_birth: formData.date_of_birth || undefined,
        emergency_contact_name: formData.emergency_contact_name || undefined,
        emergency_contact_phone: formData.emergency_contact_phone || undefined,
        role: formData.role || undefined,
        allocated_holidays: formData.allocated_holidays ?? undefined,
      };

      // Only include password if it's provided
      if (formData.password) {
        apiData.password = formData.password;
      }

      let result;
      if (isEditing && id) {
        result = await updateUserProfile({ id, data: apiData }).unwrap();
        
        // Handle color assignment/removal
        const oldColorId = existingUser?.assigned_color;
        if (selectedColorId && selectedColorId !== oldColorId) {
          // Assign new color
          await assignUserColor({ colorId: selectedColorId, userProfileId: result.id }).unwrap();
        } else if (!selectedColorId && oldColorId) {
          // Remove color
          await removeUserColor({ colorId: oldColorId, userProfileId: result.id }).unwrap();
        }
        
        toast.success('User Updated', `User "${result.full_name}" updated successfully`);
      } else {
        result = await createUserProfile(apiData).unwrap();
        
        // Assign color if selected
        if (selectedColorId) {
          await assignUserColor({ colorId: selectedColorId, userProfileId: result.id }).unwrap();
        }
        
        toast.success('User Created', `User "${result.full_name}" created successfully`);
      }

      navigate('/users');
    } catch (err: any) {
      console.error('Form submission error:', err);
      // Map backend validation errors into form error state
      const fieldErrors: Record<string, string> = {};
      // DRF typically returns {field: ["error"]}
      if (err?.data) {
        Object.entries(err.data as Record<string, any>).forEach(([key, val]) => {
          if (Array.isArray(val) && val.length > 0) {
            fieldErrors[key] = String(val[0]);
          } else if (typeof val === 'string') {
            fieldErrors[key] = val;
          }
        });
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        toast.error('Validation Error', 'Please correct the highlighted fields');
      } else {
        toast.error('Request Failed', err?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} user`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/users');
  };

  if (isEditing && isLoadingUser) {
    return (
      <AppShell>
        <PageLayout
          title="Loading User"
          subtitle="Please wait..."
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<User className="w-6 h-6" />}
        >
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </PageLayout>
      </AppShell>
    );
  }

  if (isEditing && existingUser?.status === 'deleted') {
    return (
      <AppShell>
        <PageLayout
          title="User deleted"
          subtitle="This account cannot be edited"
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<User className="w-6 h-6" />}
        >
          <ContentCard title="Account removed">
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                This user was deleted. Create a new user if you need the same username or email again.
              </p>
              <Button onClick={() => navigate('/users')}>
                Back to Users
              </Button>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  if (isEditing && loadError) {
    return (
      <AppShell>
        <PageLayout
          title="Error Loading User"
          subtitle="User not found"
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<User className="w-6 h-6" />}
        >
          <ContentCard title="User Not Found">
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                User not found
              </h3>
              <p className="text-gray-600 mb-4">
                The user you're trying to edit doesn't exist or you don't have permission to access it.
              </p>
              <Button onClick={() => navigate('/users')}>
                Back to Users
              </Button>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  const roles = rolesData?.results || [];
  const pageTitle = isEditing ? 'Edit User' : 'Create User';
  const pageSubtitle = isEditing 
    ? `Edit ${existingUser?.full_name || 'user'} profile`
    : 'Create a new user account with profile information';

  return (
    <AppShell>
      <PageLayout
        title={pageTitle}
        subtitle={pageSubtitle}
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<User className="w-6 h-6" />}
      >
        {/* Breadcrumb */}
        <div className="mb-6">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/users')}
            icon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Users
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <ContentCard title="Basic Information" subtitle="Required user account details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Username */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                  {isValidatingUsername && (
                    <span className="ml-2 text-xs text-blue-600">Checking availability...</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="username"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.username ? 'border-red-500' : 
                      usernameCheck?.is_unique === true ? 'border-green-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter username"
                  />
                  {/* Validation Status Indicator */}
                  {usernameToValidate && !isValidatingUsername && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      {usernameCheck?.is_unique === true ? (
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : usernameCheck?.is_unique === false ? (
                        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : null}
                    </div>
                  )}
                </div>
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                )}
                {!errors.username && usernameCheck?.is_unique === true && usernameToValidate && (
                  <p className="mt-1 text-sm text-green-600">Username is available</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                  {isValidatingEmail && (
                    <span className="ml-2 text-xs text-blue-600">Checking availability...</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.email ? 'border-red-500' : 
                      emailCheck?.is_unique === true ? 'border-green-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                  />
                  {/* Validation Status Indicator */}
                  {emailToValidate && !isValidatingEmail && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      {emailCheck?.is_unique === true ? (
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : emailCheck?.is_unique === false ? (
                        <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : null}
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
                {!errors.email && emailCheck?.is_unique === true && emailToValidate && (
                  <p className="mt-1 text-sm text-green-600">Email is available</p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.first_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.last_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>

              {/* Password */}
              <div className="md:col-span-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password {!isEditing && '*'}
                  {isEditing && (
                    <span className="text-gray-500 font-normal"> (leave empty to keep current password)</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-3 py-2 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={isEditing ? "Enter new password" : "Enter password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Password must be at least 8 characters long
                </p>
              </div>
            </div>
          </ContentCard>

          {/* Contact Information */}
          <ContentCard title="Contact Information" subtitle="Optional contact details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., +1234567890"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.date_of_birth ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-600">{errors.date_of_birth}</p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter complete address"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                )}
              </div>
            </div>
          </ContentCard>

          {/* Emergency Contact */}
          <ContentCard title="Emergency Contact" subtitle="Optional emergency contact information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Emergency Contact Name */}
              <div>
                <label htmlFor="emergency_contact_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  id="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.emergency_contact_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter emergency contact name"
                />
                {errors.emergency_contact_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergency_contact_name}</p>
                )}
              </div>

              {/* Emergency Contact Phone */}
              <div>
                <label htmlFor="emergency_contact_phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  id="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.emergency_contact_phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., +1234567890"
                />
                {errors.emergency_contact_phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergency_contact_phone}</p>
                )}
              </div>
            </div>
          </ContentCard>

          {/* Role Assignment */}
          <ContentCard title="Role Assignment" subtitle="Assign user role and permissions">
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                <Shield className="w-4 h-4 inline mr-1" />
                User Role
              </label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.role ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">No role assigned</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                    {role.description && ` - ${role.description}`}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Select a role to assign specific permissions to this user
              </p>
            </div>
          </ContentCard>

          {/* Color Assignment */}
          <ContentCard title="Calendar Color" subtitle="Assign a color for calendar events (optional)">
            <div>
              <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-block w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mr-1 align-middle"></span>
                Assigned Color
              </label>
              <select
                id="color"
                value={selectedColorId}
                onChange={(e) => setSelectedColorId(e.target.value)}
                disabled={isLoadingColors}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No color assigned (default gray)</option>
                {/* Show currently assigned color even if not available */}
                {isEditing && existingUser?.assigned_color_data && (
                  <option key={existingUser.assigned_color_data.id} value={existingUser.assigned_color_data.id}>
                    ● {existingUser.assigned_color_data.name}
                  </option>
                )}
                {/* Show available colors */}
                {availableColors?.map((color) => (
                  <option key={color.id} value={color.id}>
                    ● {color.name}
                  </option>
                ))}
              </select>
              {/* Color Preview */}
              {selectedColorId && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-gray-600">Preview:</span>
                  <div 
                    className="w-8 h-8 rounded-full border-2 border-gray-300"
                    style={{ 
                      backgroundColor: 
                        (isEditing && existingUser?.assigned_color_data?.id === selectedColorId)
                          ? existingUser.assigned_color_data.hex_value
                          : availableColors?.find(c => c.id === selectedColorId)?.hex_value || '#6b7280'
                    }}
                  ></div>
                  <span className="text-sm text-gray-600">
                    {(isEditing && existingUser?.assigned_color_data?.id === selectedColorId)
                      ? existingUser.assigned_color_data.name
                      : availableColors?.find(c => c.id === selectedColorId)?.name || 'Gray'}
                  </span>
                </div>
              )}
              <p className="mt-1 text-sm text-gray-500">
                This color will be used as the background for this user's calendar events
              </p>
            </div>
          </ContentCard>

          {/* Holiday Allocation */}
          <ContentCard title="Holiday Allocation" subtitle="Set annual holiday allowance for this user">
            <div>
              <label htmlFor="allocated_holidays" className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-block mr-1 align-middle">🏖️</span>
                Allocated Holidays per Year
              </label>
              <input
                type="number"
                id="allocated_holidays"
                step="0.5"
                min="0"
                max="365"
                value={formData.allocated_holidays ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = value === '' ? null : parseFloat(value);
                  handleInputChange('allocated_holidays', numValue as any);
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.allocated_holidays ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 20, 20.5, 25"
              />
              {errors.allocated_holidays && (
                <p className="mt-1 text-sm text-red-600">{errors.allocated_holidays}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Number of holidays allocated per year. Supports half days (e.g., 20.5). This value represents the current year's allocation.
              </p>
            </div>
          </ContentCard>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
              icon={<X className="w-4 h-4" />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              icon={<Save className="w-4 h-4" />}
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update User' : 'Create User')
              }
            </Button>
          </div>
        </form>
      </PageLayout>
    </AppShell>
  );
};
